import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { SCHRIFTZUG, TICKET_SHOP_URL } from "@/lib/media";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isEn = pathname.startsWith("/en");
  const prefix = isEn ? "/en" : "";
  // Sprachwechsler als echter Link auf die Gegenueber-Seite (hreflang-Pendant)
  const switchPath = isEn ? (pathname.replace(/^\/en/, "") || "/") : (`/en${pathname === "/" ? "" : pathname}`);

  const links = [
    { to: `${prefix}/#show`, label: t("nav_show"), testid: "nav-show" },
    { to: `${prefix}/tickets`, label: t("nav_tickets"), testid: "nav-tickets" },
    { to: `${prefix}/anreise`, label: t("nav_travel"), testid: "nav-travel" },
    { to: `${prefix}/sponsoren`, label: t("nav_sponsors"), testid: "nav-sponsors" },
    { to: `${prefix}/presse`, label: t("nav_press"), testid: "nav-press" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-hell-black/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to={prefix || "/"} data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={SCHRIFTZUG} alt="Krampus Show" width="160" height="67" className="h-7 md:h-9 w-auto" />
          <span className="hidden sm:flex flex-col justify-center leading-tight border-l border-hell-gold/30 pl-3 ml-3">
            <span className="font-cinzel text-[12px] tracking-[0.32em] uppercase text-hell-gold">Latschenwirt</span>
            <span className="font-cinzel text-[8px] tracking-[0.22em] uppercase text-hell-bone/50 mt-0.5">am Untersberg · Großgmain</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={l.testid}
              className="text-sm text-hell-bone/70 hover:text-hell-gold transition-colors duration-300 font-body tracking-wide">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to={switchPath}
            data-testid="lang-toggle"
            hrefLang={isEn ? "de" : "en"}
            className="flex items-center gap-1.5 text-xs tracking-widest uppercase text-hell-bone/70 hover:text-hell-gold transition-colors border border-white/15 rounded-full px-3 py-1.5"
          >
            <Globe className="w-3.5 h-3.5" />
            {isEn ? "DE" : "EN"}
          </Link>
          <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer" className="hidden md:block">
            <Button data-testid="nav-cta" className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(139,0,0,0.6)]">
              {t("nav_cta")}
            </Button>
          </a>
          <button data-testid="nav-mobile-toggle" className="lg:hidden text-hell-bone" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-hell-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.testid} to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)}
              className="font-cinzel text-lg tracking-[0.15em] uppercase text-hell-bone/80 hover:text-hell-gold">
              {l.label}
            </Link>
          ))}
          <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <Button data-testid="nav-cta-mobile" className="rounded-full bg-hell-red text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-6 mt-2">
              {t("nav_cta")}
            </Button>
          </a>
        </nav>
      )}
    </header>
  );
}
